// The one-page checkout — Direction A. Everything is settled here:
// channel + time, who's collecting, offers, payment. Totals are
// server-priced through POST orders/quote on every change; the pay step
// is an embedded Stripe sheet (Express Checkout + Payment Element) —
// never a redirect. Pay-at-store places and goes straight to tracking.

import { loadStripe } from '@stripe/stripe-js';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Component, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  ApiError, CHANNEL_LABEL, addressText, createPaymentIntent, fetchHours, fetchMe, fetchMyCard, fetchPublicTables, fetchStore, money, openNowFor, placeOrder, putMe, quoteCart, searchAddress, slotsFor, toE164,
  type AddressCandidate, type Channel, type Customer, type CustomerAddress, type HoursView, type LoyaltyCard, type PublicTable, type Quote, type StorePublic, type WireCart,
} from '../lib/api';
import { customerUserId, verifiedPhone } from '../lib/auth';
import { VerifyPhone } from '../components/VerifyPhone';
import { cartSubtotal, clearCart, setQuantity, useCart } from '../lib/cart';
import { pinnedTable } from '../lib/dinein';
import { dropCoupon, pendingCoupon } from '../lib/offers';

const DETAILS_KEY = 'rp-order-details';

/** Where the order goes: a geocoder candidate, or a saved address (its point may be missing). */
type PickedAddress = Omit<AddressCandidate, 'lat' | 'lng'> & { lat: number | null; lng: number | null; addressId?: string };
const fromSaved = (a: CustomerAddress): PickedAddress => ({ formatted: addressText(a), addressLine: a.addressLine, suburb: a.suburb, state: a.state, postcode: a.postcode, lat: a.lat, lng: a.lng, addressId: a.addressId });

export function Checkout() {
  const { rid, sid } = useParams() as { rid: string; sid: string };
  const storeKey = `${rid}/${sid}`;
  const nav = useNavigate();
  const cart = useCart(storeKey);
  const remembered = useMemo((): { name: string; phone: string } => {
    try {
      return JSON.parse(localStorage.getItem(DETAILS_KEY) ?? '') as { name: string; phone: string };
    } catch {
      return { name: '', phone: '' };
    }
  }, []);

  const [store, setStore] = useState<StorePublic | null>(null);
  const [hours, setHours] = useState<HoursView | null>(null);
  const [channel, setChannel] = useState<Channel>(cart?.channel ?? (pinnedTable(storeKey) ? 'dine_in' : 'pickup'));
  const [when, setWhen] = useState<string>('asap');
  const [name, setName] = useState(remembered.name);
  const [phone, setPhone] = useState(remembered.phone);
  const [note, setNote] = useState('');
  // an offer held from the Offers page arrives applied; the server still judges it
  const [coupon, setCoupon] = useState(() => pendingCoupon(storeKey) ?? '');
  const [couponApplied, setCouponApplied] = useState(() => pendingCoupon(storeKey) ?? '');
  const [couponNote, setCouponNote] = useState<string | null>(null);
  const [addrQuery, setAddrQuery] = useState('');
  const [candidates, setCandidates] = useState<AddressCandidate[] | null>(null);
  const [addr, setAddr] = useState<PickedAddress | null>(null);
  const [searching, setSearching] = useState(false);
  const [tables, setTables] = useState<PublicTable[]>([]);
  const [tableId, setTableId] = useState<string>(pinnedTable(storeKey)?.tableId ?? '');
  const [payMethod, setPayMethod] = useState<'online' | 'in_store'>('in_store');
  const [me, setMe] = useState<Customer | null>(null);
  // the mobile this device proved by SMS; placing waits for it (unless the SMS can't go out)
  const [verifiedAs, setVerifiedAs] = useState<string | null>(() => verifiedPhone());
  const [verifyDown, setVerifyDown] = useState(false);
  const [card, setCard] = useState<LoyaltyCard | null>(null);
  const [redeem, setRedeem] = useState(false);
  const [loyaltyNote, setLoyaltyNote] = useState<string | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paySheet, setPaySheet] = useState<{ orderId: string; paymentIntentId: string; clientSecret: string; publishableKey: string; total: number } | null>(null);
  const quoteSeq = useRef(0);

  useEffect(() => {
    fetchStore(rid, sid).then((s) => {
      setStore(s);
      document.title = `Checkout — ${s.name}`;
      if (!s.pickupEnabled && channel === 'pickup') setChannel(s.deliveryEnabled ? 'delivery' : 'dine_in');
      if (s.onlinePaymentsEnabled) setPayMethod('online');
    });
    fetchHours(rid, sid).then(setHours);
    fetchPublicTables(rid, sid).then(setTables, () => setTables([]));
    if (customerUserId()) loadAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid, sid]);

  // the account behind this device, if it has one: details, saved addresses, the card
  function loadAccount() {
    fetchMe(rid).then((m) => {
      if (!m) return;
      setMe(m);
      setName((n) => n || m.name);
      setPhone((p) => p || m.phoneE164 || '');
    }, () => { /* the checkout works without it */ });
    fetchMyCard(rid).then(setCard, () => setCard(null));
  }

  // delivery starts at the default saved address
  useEffect(() => {
    if (channel !== 'delivery' || addr) return;
    const preferred = me?.addresses.find((a) => a.isDefault);
    if (preferred) setAddr(fromSaved(preferred));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, me]);

  // the cart on the wire — null while something the channel needs is missing
  const wire = useMemo((): WireCart | null => {
    if (!cart || cart.lines.length === 0) return null;
    const w: WireCart = {
      source: 'website',
      collectionType: channel,
      requestedFor: when === 'asap' ? null : when,
      lines: cart.lines.map((l) => l.wire),
    };
    // the customer block rides the quote only once it is real — a placeholder phone is nobody
    const e164 = toE164(phone);
    // the profile's row when the number is the profile's — the card is read from it
    if (name.trim() && e164) w.customer = { customerId: me?.phoneE164 === e164 ? me.customerId : null, name: name.trim(), phoneE164: e164 };
    if (note.trim()) w.comments = note.trim();
    if (couponApplied) w.discounts = [{ discountType: 'coupon', label: `Coupon ${couponApplied}`, couponCode: couponApplied }];
    if (redeem) w.redeemLoyalty = true;
    if (channel === 'delivery') {
      if (!addr) return null; // no located address yet — nothing to price
      w.delivery = { addressLine: addr.addressLine, suburb: addr.suburb, state: addr.state, postcode: addr.postcode, lat: addr.lat, lng: addr.lng };
    }
    if (channel === 'dine_in') {
      const t = tables.find((x) => x.tableId === tableId);
      if (!t) return null;
      w.dineIn = { tableId: t.tableId, tableName: t.name };
    }
    return w;
  }, [cart, channel, when, name, phone, note, couponApplied, redeem, me, addr, tables, tableId]);

  // server-priced totals on every meaningful change
  useEffect(() => {
    if (!wire) {
      setQuote(null);
      return;
    }
    const seq = ++quoteSeq.current;
    const t = setTimeout(() => {
      quoteCart(rid, sid, wire).then(
        (q) => { if (seq === quoteSeq.current) { setQuote(q); setQuoteError(null); } },
        (e) => {
          if (seq !== quoteSeq.current) return;
          setQuote(null);
          setQuoteError(e instanceof ApiError ? e.message : 'Could not price the order.');
          if (e instanceof ApiError && (e.code.startsWith('coupon') || e.code === 'one_coupon')) {
            setCouponApplied('');
            setCouponNote(e.message);
            dropCoupon(storeKey);
          }
          if (e instanceof ApiError && e.code.startsWith('loyalty')) {
            setRedeem(false);
            setLoyaltyNote(e.message);
          }
        },
      );
    }, 300);
    return () => clearTimeout(t);
  }, [rid, sid, storeKey, wire]);

  // the slot list re-judges itself every minute: the lead time moves with the clock
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setClock(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const slots = useMemo(() => slotsFor(hours, channel, clock), [hours, channel, clock]);

  async function findAddress() {
    if (addrQuery.trim().length < 4 || searching) return;
    setSearching(true);
    setCandidates(null);
    setAddr(null);
    try {
      setCandidates(await searchAddress(rid, sid, addrQuery.trim()));
    } catch {
      setCandidates([]);
    } finally {
      setSearching(false);
    }
  }

  async function place() {
    if (!wire || busy) return;
    const e164 = toE164(phone);
    if (!name.trim() || !e164) {
      setError('Add your name and a mobile number the store can reach.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      localStorage.setItem(DETAILS_KEY, JSON.stringify({ name: name.trim(), phone: phone.trim() }));
      // the profile behind this device's identity: best effort, so "My orders" knows me.
      // PUT customer/me replaces the whole profile — carry the account's own choices through.
      let customerId: string | null = me?.phoneE164 === e164 && me.name === name.trim() ? me.customerId : null;
      // only a verified number may hold a profile (phone_unverified otherwise)
      if (!customerId && verifiedAs === e164) {
        try {
          customerId = (await putMe(rid, { name: name.trim(), phone: e164, email: me?.email ?? null, marketingSmsOptIn: me?.marketingSmsOptIn ?? false, marketingEmailOptIn: me?.marketingEmailOptIn ?? false })).customerId;
        } catch {
          /* the order still carries name + phone */
        }
      }
      const placed = await placeOrder(rid, sid, { ...wire, customer: { customerId, name: name.trim(), phoneE164: e164 } });
      dropCoupon(storeKey);
      if (payMethod === 'online') {
        try {
          const intent = await createPaymentIntent(rid, sid, placed.orderId);
          clearCart();
          setPaySheet({ orderId: placed.orderId, paymentIntentId: intent.paymentIntentId, clientSecret: intent.clientSecret, publishableKey: intent.publishableKey, total: placed.grandTotalInCents });
          return; // the pay sheet takes over
        } catch (e) {
          if (e instanceof ApiError && e.code === 'stripe_not_configured') {
            // the store cannot take online payments — the order stands, pay at the store
            clearCart();
            nav(`/r/${rid}/s/${sid}/track/${placed.orderId}?paynote=1`, { replace: true });
            return;
          }
          throw e;
        }
      }
      clearCart();
      nav(`/r/${rid}/s/${sid}/track/${placed.orderId}`, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not place the order.');
    } finally {
      setBusy(false);
    }
  }

  if ((!cart || cart.lines.length === 0) && !paySheet) {
    return (
      <div className="co-empty">
        <p>Your order is empty.</p>
        <Link to={`/r/${rid}/s/${sid}`} className="co-back">← Back to the menu</Link>
      </div>
    );
  }

  const channels = (['pickup', 'delivery', 'dine_in'] as Channel[]).filter((c) => (c === 'pickup' ? store?.pickupEnabled : c === 'delivery' ? store?.deliveryEnabled : store?.dineInEnabled));
  const total = quote?.grandTotalInCents ?? cartSubtotal(cart);
  const open = openNowFor(hours, channel);
  const phoneReady = (!!verifiedAs && verifiedAs === toE164(phone)) || verifyDown;

  return (
    <div className="co-page">
      <header className="co-head">
        <Link to={`/r/${rid}/s/${sid}`} className="co-backbtn" aria-label="Back to the menu">‹</Link>
        <div>
          <div className="co-store">{store?.name ?? '…'}</div>
          <h1>Checkout</h1>
        </div>
      </header>

      <section className="co-sec" data-section="collection">
        <h2>Collection</h2>
        <div className="co-modes">
          {channels.map((c) => (
            <button key={c} className={'mode' + (channel === c ? ' on' : '')} aria-pressed={channel === c} onClick={() => setChannel(c)}>{CHANNEL_LABEL[c]}</button>
          ))}
        </div>
        <select className="co-input" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="Collection time">
          <option value="asap" disabled={!open}>{open ? 'ASAP' : 'ASAP — closed right now'}</option>
          {slots.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {!open && <div className="co-hint">The store is closed right now — pick a time and we'll schedule it.</div>}
        {channel === 'delivery' && (
          <div data-section="address">
            {!addr ? (
              <>
                {me && me.addresses.length > 0 && (
                  <div data-section="saved-addresses">
                    {me.addresses.map((a) => (
                      <button key={a.addressId} className="optrow" aria-label={`Deliver to ${a.label || a.addressLine}`} data-address-id={a.addressId} onClick={() => setAddr(fromSaved(a))}>
                        <span className="rad" />
                        <span className="co-saved">{a.label && <b>{a.label}</b>}{addressText(a)}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="co-row2">
                  <input className="co-input" placeholder="Delivery address" aria-label="Delivery address" value={addrQuery} onChange={(e) => setAddrQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && findAddress()} />
                  <button className="co-apply" onClick={findAddress} disabled={searching || addrQuery.trim().length < 4}>{searching ? '…' : 'Find'}</button>
                </div>
                {candidates?.map((c) => (
                  <button key={c.formatted} className="optrow" aria-label={`Deliver to ${c.formatted}`} onClick={() => setAddr(c)}><span className="rad" />{c.formatted}</button>
                ))}
                {candidates?.length === 0 && <div className="co-err">Couldn't find that address — try adding the suburb.</div>}
              </>
            ) : (
              <div className="co-offer-ok" data-address={addr.formatted} data-address-id={addr.addressId}>
                {addr.formatted}
                <button onClick={() => { setAddr(null); setCandidates(null); }}>change</button>
              </div>
            )}
          </div>
        )}
        {channel === 'dine_in' && (
          <select className="co-input" value={tableId} onChange={(e) => setTableId(e.target.value)} aria-label="Your table">
            <option value="">Choose your table…</option>
            {tables.map((t) => <option key={t.tableId} value={t.tableId}>{t.name}</option>)}
          </select>
        )}
      </section>

      <section className="co-sec" data-section="details">
        <h2>Your details</h2>
        <input className="co-input" placeholder="Name" aria-label="Name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="co-input" placeholder="Mobile number" aria-label="Mobile number" autoComplete="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <div className="co-hint">The store reaches you on this number if anything comes up.</div>
        <VerifyPhone
          phone={toE164(phone)}
          verified={!!verifiedAs && verifiedAs === toE164(phone)}
          onVerified={(v) => { setVerifiedAs(v.user.phoneE164); loadAccount(); }}
          onUnavailable={() => setVerifyDown(true)}
        />
        <input className="co-input" placeholder="A note for the kitchen (optional)" aria-label="A note for the kitchen" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </section>

      <section className="co-sec" data-section="offers">
        <h2>Offers</h2>
        <div className="co-row2">
          <input className="co-input" placeholder="Coupon code" aria-label="Coupon code" value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponNote(null); }} />
          <button className="co-apply" onClick={() => { setCouponNote(null); setCouponApplied(coupon.trim()); }} disabled={!coupon.trim()}>Apply</button>
        </div>
        {couponApplied && quote?.discounts.some((d) => d.discountType === 'coupon') && (
          <div className="co-offer-ok" data-coupon={couponApplied}>
            {quote.discounts.find((d) => d.discountType === 'coupon')!.label} — − {money(quote.discounts.find((d) => d.discountType === 'coupon')!.amountInCents)}
            <button onClick={() => { setCouponApplied(''); setCoupon(''); dropCoupon(storeKey); }}>remove</button>
          </div>
        )}
        {couponNote && <div className="co-err" role="alert">{couponNote}</div>}
        {card?.isRedeemable && (
          <button className="optrow" aria-label="Redeem my loyalty card" aria-pressed={redeem} data-card={card.cardId} onClick={() => { setLoyaltyNote(null); setRedeem(!redeem); }}>
            <span className={'chk' + (redeem ? ' on' : '')} />
            Use my loyalty card
            <span className="price">− {money(card.valueInCents)}</span>
          </button>
        )}
        {loyaltyNote && <div className="co-err" role="alert">{loyaltyNote}</div>}
      </section>

      <section className="co-sec" data-section="payment">
        <h2>Payment</h2>
        {store?.onlinePaymentsEnabled && (
          <button className="optrow" aria-label="Pay by card" aria-pressed={payMethod === 'online'} onClick={() => setPayMethod('online')}>
            <span className={'rad' + (payMethod === 'online' ? ' on' : '')} />
            Card · Apple Pay · Google Pay
            <span className="price">Stripe</span>
          </button>
        )}
        <button className="optrow" aria-label="Pay at the store" aria-pressed={payMethod === 'in_store'} onClick={() => setPayMethod('in_store')}>
          <span className={'rad' + (payMethod === 'in_store' ? ' on' : '')} />
          Pay at the store
        </button>
      </section>

      <section className="co-sec" data-section="summary">
        <h2>Your order</h2>
        {cart?.lines.map((l) => (
          <div className="co-line" key={l.lineId} data-line={l.lineId}>
            <span className="q">{l.wire.quantity}×</span>
            <span className="nm">{l.name}
              {l.configSummary && <span className="cfg">{l.configSummary}</span>}
              <span className="cfg">
                <button className="linky" aria-label={`One fewer ${l.name}`} onClick={() => setQuantity(storeKey, l.lineId, l.wire.quantity - 1)}>− one</button>
                {' · '}
                <button className="linky" aria-label={`One more ${l.name}`} onClick={() => setQuantity(storeKey, l.lineId, l.wire.quantity + 1)}>+ one</button>
              </span>
            </span>
            <span className="amt money">{money(l.wire.quantity * l.unitPriceInCents)}</span>
          </div>
        ))}
        <div className="co-totals">
          {quote ? (
            <>
              <div className="tr"><span>Subtotal</span><span data-subtotal>{money(quote.subtotalInCents)}</span></div>
              {quote.discounts.map((d) => <div className="tr neg" key={d.rowId}><span>{d.label}</span><span>− {money(d.amountInCents)}</span></div>)}
              {quote.surcharges.map((d) => <div className="tr" key={d.rowId}><span>{d.label}</span><span>{money(d.amountInCents)}</span></div>)}
              {quote.deliveryFeeInCents > 0 && <div className="tr"><span>Delivery</span><span data-delivery-fee>{money(quote.deliveryFeeInCents)}</span></div>}
              <div className="tr grand"><span>Total</span><span data-total>{money(quote.grandTotalInCents)}</span></div>
              <div className="gstline">includes {money(quote.gstInCents)} GST</div>
            </>
          ) : (
            <div className="tr grand"><span>Total</span><span data-total-estimate>{money(total)}</span></div>
          )}
          {quoteError && <div className="co-err" role="alert">{quoteError}</div>}
        </div>
      </section>

      {error && <div className="co-err" role="alert" style={{ margin: '0 18px' }}>{error}</div>}

      {wire && !phoneReady && (
        <div className="co-hint" style={{ textAlign: 'center' }} data-needs-verify>Verify your number to place the order.</div>
      )}
      {!wire && cart && cart.lines.length > 0 && (
        <div className="co-hint" style={{ textAlign: 'center' }}>
          {channel === 'delivery' ? 'Find and pick your delivery address to continue.' : channel === 'dine_in' ? 'Choose your table to continue.' : ''}
        </div>
      )}
      <div className="co-placebar">
        <button className="co-place" onClick={place} disabled={busy || !!paySheet || !wire || !quote || !phoneReady}>
          {busy ? 'Placing…' : payMethod === 'online' ? `Pay ${money(total)} · Place order` : `Place order · ${money(total)}`}
        </button>
        <div className="co-fine">Priced by the kitchen — no surprises at the counter.</div>
      </div>

      {paySheet && <PaySheet rid={rid} sid={sid} {...paySheet} onClose={() => nav(`/r/${rid}/s/${sid}/track/${paySheet.orderId}`, { replace: true })} />}
    </div>
  );
}

// ── the embedded pay sheet ───────────────────────────────────────────

function PaySheet(props: { rid: string; sid: string; orderId: string; paymentIntentId: string; clientSecret: string; publishableKey: string; total: number; onClose: () => void }) {
  const stripePromise = useMemo(() => loadStripe(props.publishableKey), [props.publishableKey]);
  return (
    <div className="scrim">
      <div className="sheet" role="dialog" aria-label="Pay" data-section="pay-sheet" data-order={props.orderId} data-intent={props.paymentIntentId} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <button className="sclose" onClick={props.onClose} aria-label="Pay later at the store">✕</button>
        <div className="sbody" style={{ padding: '18px 18px 8px' }}>
          <div className="paytt"><span>Pay</span><span className="money">{money(props.total)}</span></div>
          <CardFormBoundary>
            <Elements stripe={stripePromise} options={{ clientSecret: props.clientSecret, appearance: { variables: { colorPrimary: '#1faa89', colorText: '#1c1d3e', borderRadius: '10px', fontFamily: 'Chivo, system-ui, sans-serif' } } }}>
              <PayInner {...props} />
            </Elements>
          </CardFormBoundary>
          <div className="co-fine" style={{ marginTop: 10 }}>Your order is placed either way — close this to pay at the store.</div>
        </div>
      </div>
    </div>
  );
}

function PayInner(props: { rid: string; sid: string; orderId: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const nav = useNavigate();
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function confirm() {
    if (!stripe || !elements) return;
    setPaying(true);
    setErr(null);
    const { error } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (error) {
      setErr(error.message ?? 'Payment failed — nothing was charged.');
      setPaying(false);
      return;
    }
    nav(`/r/${props.rid}/s/${props.sid}/track/${props.orderId}`, { replace: true });
  }

  return (
    <>
      <ExpressCheckoutElement onConfirm={confirm} />
      <div className="payor">or pay with card</div>
      <PaymentElement />
      {err && <div className="co-err" role="alert">{err}</div>}
      <button className="co-place" style={{ marginTop: 14 }} onClick={confirm} disabled={paying}>{paying ? 'Paying…' : 'Pay now'}</button>
      <div className="co-fine">Secured by Stripe — your card never leaves this page.</div>
    </>
  );
}

/** The card form is a third party on the page: if it throws, the order
 * (already placed) and the page survive, and the customer pays at the store. */
class CardFormBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  render(): ReactNode {
    if (this.state.failed) return <div className="co-err" role="alert" data-card-form="failed">The card form could not load. Your order is placed — pay at the store when you collect it.</div>;
    return this.props.children;
  }
}
